<template>
    <Transition name="fade">
        <div v-if="show" class="modal-overlay" @click.self="$emit('cancel')">
            <div class="modal-content" ref="modalContent">
                <h2>{{ title }}</h2>
                <p>
                    <slot></slot>
                </p>

                <div class="modal-buttons">
                    <button
                        class="modal-button cancel-button"
                        @click="$emit('cancel')"
                        ref="cancelButton"
                    >
                        {{ cancelText }}
                    </button>
                    <button
                        class="modal-button confirm-button"
                        @click="confirmAction"
                        ref="confirmButton"
                    >
                        {{ confirmText }}
                    </button>
                </div>

                <!-- Timer bar (only shown if useTimer is true) -->
                <div v-if="useTimer" class="timer-container">
                    <div class="timer-bar" :style="{ width: `${timerProgress}%` }"></div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script setup>
import {ref, watch, onMounted, onUnmounted, nextTick} from "vue"
import gsap from "gsap"
import {setActiveContext, initModalKeyboard} from "@/services/keyboard.js"
import {params} from "@/systemInfo.js"

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        required: true,
    },
    confirmText: {
        type: String,
        default: "Yes",
    },
    cancelText: {
        type: String,
        default: "No",
    },
    useTimer: {
        type: Boolean,
        default: false,
    },
    timerDuration: {
        type: Number,
        default: params.modalTimeout || 10,
    },
})

const emit = defineEmits(["confirm", "cancel"])
const confirmButton = ref(null)
const cancelButton = ref(null)
const modalContent = ref(null)
const hasConfirmed = ref(false)
const timerProgress = ref(100)
let timerInterval = null

// Confirm action
const confirmAction = () => {
    if (hasConfirmed.value) return // Prevent double confirmation
    hasConfirmed.value = true
    emit("confirm")
}

// Cancel action
const cancelAction = () => {
    emit("cancel")
}

// Start the timer
const startTimer = () => {
    if (!props.useTimer) return

    // Reset timer
    timerProgress.value = 100
    clearInterval(timerInterval)

    // Start countdown
    const startTime = Date.now()
    timerInterval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000
        timerProgress.value = 100 - (elapsed / props.timerDuration) * 100

        if (timerProgress.value <= 0) {
            clearInterval(timerInterval)
            // confirmAction()
            cancelAction()
        }
    }, 100)
}

// Animate modal opening
const animateModalOpen = () => {
    gsap.fromTo(
        modalContent.value,
        {
            opacity: 0,
            y: 20,
            scale: 0.95,
        },
        {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.3,
            ease: "power2.out",
        },
    )
}

// Animate modal closing
const animateModalClose = () => {
    return new Promise((resolve) => {
        gsap.to(modalContent.value, {
            opacity: 0,
            y: 20,
            scale: 0.95,
            duration: 0.3,
            ease: "power2.in",
            onComplete: resolve,
        })
    })
}

// Get buttons for keyboard navigation
const getButtons = () => {
    return [cancelButton.value, confirmButton.value].filter(Boolean)
}

// Watch for show changes
watch(
    () => props.show,
    (newValue, oldValue) => {
        if (newValue) {
            // Modal is opening
            hasConfirmed.value = false

            // Focus the confirm button when modal opens
            nextTick(() => {
                if (confirmButton.value) {
                    confirmButton.value.focus()
                }

                // Initialize keyboard navigation for modal
                const buttons = getButtons()

                // Set active context to modal
                setActiveContext("modal", {
                    buttons,
                    onConfirm: confirmAction,
                    onCancel: cancelAction,
                })

                // Initialize modal keyboard navigation
                initModalKeyboard(buttons, confirmAction, cancelAction)

                // Animate modal opening
                animateModalOpen()
            })

            // Start the timer if needed
            if (props.useTimer) {
                startTimer()
            }
        } else if (oldValue) {
            // Modal is closing
            clearInterval(timerInterval)
        }
    },
)

// Expose methods to parent components
defineExpose({
    animateModalClose,
    getButtons,
    confirmButton,
    cancelButton,
})
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--color-overlay);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    border-radius: var(--border-radius-md);
    border: var(--border-thin) var(--color-primary-light);
    padding: var(--spacing-lg);
    width: 90%;
    max-width: calc(var(--cqw) * 35);
    box-shadow: var(--shadow-md);
    text-align: center;
    transform-origin: center;
    animation: modalEnter var(--transition-normal) forwards;

    background: var(--color-popup-dark);
    backdrop-filter: var(--blur-popup);
	-webkit-backdrop-filter: var(--blur-popup);
}

@keyframes modalEnter {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

h2 {
    font-size: var(--font-size-lg);
    margin-bottom: var(--spacing-md);
    color: var(--color-text);
}

p {
    font-size: var(--font-size-reg);
    margin-bottom: var(--spacing-lg);
    color: var(--color-text-secondary);
}

.timer-container {
    width: 100%;
    height: var(--font-size-sm);
    background-color: var(--color-border);
    border-radius: var(--border-radius-full);
    margin-top: var(--spacing-lg);
    overflow: hidden;
}

.timer-bar {
    height: 100%;
    border-radius: var(--border-radius-full);
    background: var(--gradient-primary);
    transition: width var(--transition-linear);
}

.modal-buttons {
    display: flex;
    justify-content: center;
    gap: var(--spacing-md);
}

.modal-button {
    min-width: 20%;
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: var(--border-radius-sm);
    font-size: var(--font-size-reg);
    font-weight: var(--font-weight-semibold);
    cursor: pointer;
    transition: all var(--transition-normal);
    border: none;
    color: var(--color-text);
    text-shadow: var(--shadow-sm);
    background: var(--color-popup-element-light);
}

/* .cancel-button { */
/*     background-color: var(--color-surface-light); */
/* } */

/* .cancel-button:focus, .cancel-button:hover { */
/*     background-color: var(--color-surface); */
/* } */

/* !* .confirm-button { *! */
/* !*     background: var(--gradient-primary); *! */
/* !* } *! */

/* .confirm-button:focus, .confirm-button:hover { */
/*     background: var(--gradient-primary-dark); */
/* } */

/* Transition classes */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>